// src/pages/LiveStreams/LiveStreamsPage.tsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Radio, 
  Play, 
  Clock, 
  Users, 
  Eye, 
  Calendar, 
  Video,
  Heart,
  MessageCircle
} from 'lucide-react';
import MainLayout from '../../components/MainLayout/MainLayout';
import SecureImage from '../../components/Media/SecureImage';
import liveStreamService, { type LiveStream } from '../../services/liveStreamService';
import useSEO from '../../hooks/useSEO';

// Live Stream Card Component
interface LiveStreamCardProps {
  stream: LiveStream;
  isLive?: boolean;
}

const LiveStreamCard = ({ stream, isLive }: LiveStreamCardProps) => {
  const getStatusBadge = () => {
    if (stream.status === 'LIVE') {
      return (
        <span className="absolute top-3 left-3 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-md flex items-center gap-1 animate-pulse">
          <Radio className="w-3 h-3" />
          LIVE
        </span>
      );
    }
    if (stream.status === 'SCHEDULED') {
      return (
        <span className="absolute top-3 left-3 px-2 py-1 bg-orange-600 text-white text-xs font-medium rounded-md flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Scheduled
        </span>
      );
    }
    if (stream.status === 'ENDED' && stream.recordingUrl) {
      return (
        <span className="absolute top-3 left-3 px-2 py-1 bg-slate-700 text-white text-xs font-medium rounded-md flex items-center gap-1">
          <Play className="w-3 h-3" />
          Replay
        </span>
      );
    }
    if (stream.status === 'ENDED') {
      return (
        <span className="absolute top-3 left-3 px-2 py-1 bg-slate-600 text-white text-xs font-medium rounded-md flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Ended
        </span>
      );
    }
    return null;
  };

  return (
    <Link 
      to={`/live/${stream.slug}`} 
      className="group block rounded-xl overflow-hidden bg-slate-800/50 backdrop-blur-sm border border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/10"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video">
        {stream.thumbnailUrl ? (
          <SecureImage 
            src={stream.thumbnailUrl}
            alt={stream.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-900/50 to-slate-900 flex items-center justify-center">
            <Video className="w-12 h-12 text-orange-400/50" />
          </div>
        )}
        
        {/* Status Badge */}
        {getStatusBadge()}
        
        {/* Live Viewer Count */}
        {isLive && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 rounded-md flex items-center gap-1 text-xs text-white">
            <Eye className="w-3 h-3 text-red-400" />
            {liveStreamService.formatViewerCount(stream.currentViewers)} watching
          </div>
        )}
        
        {/* Duration for replays */}
        {stream.status === 'ENDED' && stream.actualStartTime && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 rounded-md text-xs text-white">
            {liveStreamService.getStreamDuration(stream)}
          </div>
        )}
        
        {/* Play Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-orange-600/90 flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform duration-300">
            <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white text-lg line-clamp-2 mb-2 group-hover:text-orange-300 transition-colors">
          {stream.title}
        </h3>
        
        {/* Host Info */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white text-sm font-medium">
            {stream.host.firstName?.charAt(0) || stream.host.username.charAt(0)}
          </div>
          <span className="text-gray-400 text-sm">
            {stream.host.firstName && stream.host.lastName 
              ? `${stream.host.firstName} ${stream.host.lastName}`
              : stream.host.username}
          </span>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {stream.status === 'LIVE' ? (
            <>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {liveStreamService.formatViewerCount(stream.currentViewers)}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="w-3 h-3" />
                {liveStreamService.formatViewerCount(stream.likeCount)}
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                {liveStreamService.formatViewerCount(stream.commentCount)}
              </span>
            </>
          ) : stream.status === 'SCHEDULED' && stream.scheduledStartTime ? (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(stream.scheduledStartTime).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {liveStreamService.formatViewerCount(stream.totalViews)} views
              </span>
              <span className="flex items-center gap-1">
                <Heart className="w-3 h-3" />
                {liveStreamService.formatViewerCount(stream.likeCount)}
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

// Featured Live Stream Card (larger for the hero section)
const FeaturedLiveStream = ({ stream }: { stream: LiveStream }) => {
  return (
    <Link 
      to={`/live/${stream.slug}`} 
      className="group relative block rounded-2xl overflow-hidden"
    >
      <div className="relative aspect-[21/9] min-h-[300px]">
        {stream.thumbnailUrl ? (
          <SecureImage 
            src={stream.thumbnailUrl}
            alt={stream.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-900 via-slate-900 to-red-900" />
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
          {/* Live Badge */}
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md flex items-center gap-2 animate-pulse">
              <Radio className="w-4 h-4" />
              LIVE NOW
            </span>
            <span className="px-3 py-1.5 bg-black/50 backdrop-blur-sm text-white text-sm rounded-md flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {liveStreamService.formatViewerCount(stream.currentViewers)} watching
            </span>
          </div>
          
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 line-clamp-2 group-hover:text-orange-300 transition-colors">
            {stream.title}
          </h2>
          
          {stream.description && (
            <p className="text-gray-300 text-sm md:text-base line-clamp-2 mb-4 max-w-2xl">
              {stream.description}
            </p>
          )}
          
          {/* Host Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-medium">
              {stream.host.firstName?.charAt(0) || stream.host.username.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium">
                {stream.host.firstName && stream.host.lastName 
                  ? `${stream.host.firstName} ${stream.host.lastName}`
                  : stream.host.username}
              </p>
              <p className="text-gray-400 text-sm">Host</p>
            </div>
          </div>
        </div>
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-20 h-20 rounded-full bg-orange-600/90 backdrop-blur-sm flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform duration-300">
            <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Section Header Component
const SectionHeader = ({ title, icon: Icon }: { title: string; icon: React.ElementType }) => (
  <div className="flex items-center gap-3 mb-6">
    <Icon className="w-6 h-6 text-orange-400" />
    <h2 className="text-2xl font-bold text-white">{title}</h2>
  </div>
);

// Empty State Component
const EmptyState = ({ title, description, icon: Icon }: { title: string; description: string; icon: React.ElementType }) => (
  <div className="text-center py-12">
    <Icon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-gray-400 mb-2">{title}</h3>
    <p className="text-gray-500">{description}</p>
  </div>
);

const LiveStreamsPage = () => {
  // Apply SEO
  useSEO();
  
  const [liveStreams, setLiveStreams] = useState<LiveStream[]>([]);
  const [upcomingStreams, setUpcomingStreams] = useState<LiveStream[]>([]);
  const [pastStreams, setPastStreams] = useState<LiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ liveCount: 0, totalViewers: 0 });
  
  // Pagination states
  const [pastPage, setPastPage] = useState(0);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all stream types in parallel
        const [liveResponse, upcomingResponse, pastResponse, overallStats] = await Promise.all([
          liveStreamService.getLiveStreams(0, 20),
          liveStreamService.getUpcomingStreams(0, 10),
          liveStreamService.getPastStreams(0, 12, false), // Show all past streams, not just recordings
          liveStreamService.getOverallStats()
        ]);
        
        setLiveStreams(liveResponse.content);
        setUpcomingStreams(upcomingResponse.content);
        setPastStreams(pastResponse.content);
        setHasMorePast(!pastResponse.last);
        setStats({
          liveCount: overallStats.liveStreamCount,
          totalViewers: overallStats.totalLiveViewers
        });
      } catch (err) {
        console.error('Error fetching streams:', err);
        setError('Failed to load live streams. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();
    
    // Poll for updates every 30 seconds
    const pollInterval = setInterval(async () => {
      try {
        const [liveResponse, overallStats] = await Promise.all([
          liveStreamService.getLiveStreams(0, 20),
          liveStreamService.getOverallStats()
        ]);
        setLiveStreams(liveResponse.content);
        setStats({
          liveCount: overallStats.liveStreamCount,
          totalViewers: overallStats.totalLiveViewers
        });
      } catch (error) {
        console.warn('Error polling live streams:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, []);

  const loadMorePastStreams = async () => {
    if (loadingMore || !hasMorePast) return;
    
    try {
      setLoadingMore(true);
      const nextPage = pastPage + 1;
      const response = await liveStreamService.getPastStreams(nextPage, 12, false); // Show all past streams
      
      setPastStreams(prev => [...prev, ...response.content]);
      setPastPage(nextPage);
      setHasMorePast(!response.last);
    } catch (error) {
      console.error('Error loading more streams:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Get featured live stream (most viewers)
  const featuredStream = liveStreams.length > 0 
    ? [...liveStreams].sort((a, b) => b.currentViewers - a.currentViewers)[0] 
    : null;
  
  // Other live streams (excluding featured)
  const otherLiveStreams = featuredStream 
    ? liveStreams.filter(s => s.id !== featuredStream.id) 
    : [];

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Loading skeleton */}
            <div className="animate-pulse">
              <div className="h-8 w-48 bg-slate-700 rounded mb-8" />
              <div className="h-64 bg-slate-800 rounded-2xl mb-12" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-slate-800 rounded-xl aspect-video" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black pt-8 flex items-center justify-center">
          <div className="text-center">
            <Video className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Unable to Load Streams</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-black pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-r from-red-500 to-red-500">
                <Radio className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">Live Streams</h1>
                <p className="text-gray-400 mt-1">
                  Watch live content and replays from your favorite creators
                </p>
              </div>
            </div>
            
            {/* Live Stats Banner */}
            {stats.liveCount > 0 && (
              <div className="inline-flex items-center gap-4 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full">
                <span className="flex items-center gap-2 text-red-400">
                  <Radio className="w-4 h-4 animate-pulse" />
                  <span className="font-semibold">{stats.liveCount}</span>
                  <span className="text-sm">live now</span>
                </span>
                <span className="w-px h-4 bg-red-500/30" />
                <span className="flex items-center gap-2 text-gray-300">
                  <Users className="w-4 h-4" />
                  <span className="font-semibold">{liveStreamService.formatViewerCount(stats.totalViewers)}</span>
                  <span className="text-sm">total viewers</span>
                </span>
              </div>
            )}
          </div>
          
          {/* Featured Live Stream */}
          {featuredStream && (
            <section className="mb-12">
              <FeaturedLiveStream stream={featuredStream} />
            </section>
          )}
          
          {/* Live Now Section */}
          {otherLiveStreams.length > 0 && (
            <section className="mb-12">
              <SectionHeader title="Live Now" icon={Radio} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {otherLiveStreams.map(stream => (
                  <LiveStreamCard key={stream.id} stream={stream} isLive />
                ))}
              </div>
            </section>
          )}
          
          {/* No Live Streams - Show placeholder */}
          {liveStreams.length === 0 && (
            <section className="mb-12">
              <div className="rounded-2xl bg-gradient-to-br from-slate-800/50 to-orange-900/20 border border-white/10 p-8 text-center">
                <Radio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Live Streams Right Now</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Check back later or browse our past streams below to watch replays of your favorite content.
                </p>
                {upcomingStreams.length > 0 && (
                  <p className="text-orange-400 text-sm">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {upcomingStreams.length} upcoming stream{upcomingStreams.length !== 1 ? 's' : ''} scheduled
                  </p>
                )}
              </div>
            </section>
          )}
          
          {/* Upcoming Streams */}
          {upcomingStreams.length > 0 && (
            <section className="mb-12">
              <SectionHeader title="Coming Up" icon={Calendar} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {upcomingStreams.map(stream => (
                  <LiveStreamCard key={stream.id} stream={stream} />
                ))}
              </div>
            </section>
          )}
          
          {/* Past Streams / Replays */}
          <section>
            <SectionHeader title="Past Streams" icon={Clock} />
            
            {pastStreams.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {pastStreams.map(stream => (
                    <LiveStreamCard key={stream.id} stream={stream} />
                  ))}
                </div>
                
                {/* Load More Button */}
                {hasMorePast && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={loadMorePastStreams}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg border border-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        'Load More'
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <EmptyState 
                title="No Past Streams Yet"
                description="Past streams with recordings will appear here for you to rewatch."
                icon={Video}
              />
            )}
          </section>
          
        </div>
      </div>
    </MainLayout>
  );
};

export default LiveStreamsPage;
